/**
 * Cryptographic utility functions
 *
 * Provides encoding, hashing, and conversion helpers for crypto operations
 */

import { sha256 } from '@noble/hashes/sha256';

// ============================================================================
// Byte Array Conversions
// ============================================================================

/**
 * Convert hex string to Uint8Array
 *
 * @param hex - Hex string (with or without 0x prefix)
 * @returns Byte array
 */
export function hexToBytes(hex: string): Uint8Array {
  // Remove 0x prefix if present
  if (hex.startsWith('0x')) {
    hex = hex.slice(2);
  }

  // Ensure even length
  if (hex.length % 2 !== 0) {
    throw new Error('Hex string must have even length');
  }

  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Convert Uint8Array to hex string
 *
 * @param bytes - Byte array
 * @returns Lowercase hex string (no 0x prefix)
 */
export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert base64 string to Uint8Array
 *
 * @param base64 - Base64 string
 * @returns Byte array
 */
export function base64ToBytes(base64: string): Uint8Array {
  // Browser environment
  if (typeof window !== 'undefined' && window.atob) {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  // Node.js environment
  return new Uint8Array(Buffer.from(base64, 'base64'));
}

/**
 * Convert Uint8Array to base64 string
 *
 * @param bytes - Byte array
 * @returns Base64 string
 */
export function bytesToBase64(bytes: Uint8Array): string {
  // Browser environment
  if (typeof window !== 'undefined' && window.btoa) {
    const binaryString = Array.from(bytes)
      .map(b => String.fromCharCode(b))
      .join('');
    return window.btoa(binaryString);
  }

  // Node.js environment
  return Buffer.from(bytes).toString('base64');
}

/**
 * Convert UTF-8 string to Uint8Array
 *
 * @param str - UTF-8 string
 * @returns Byte array
 */
export function stringToBytes(str: string): Uint8Array {
  // Use TextEncoder in browser
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str);
  }

  // Fallback for older environments
  return new Uint8Array(Buffer.from(str, 'utf-8'));
}

/**
 * Convert Uint8Array to UTF-8 string
 *
 * @param bytes - Byte array
 * @returns UTF-8 string
 */
export function bytesToString(bytes: Uint8Array): string {
  // Use TextDecoder in browser
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }

  // Fallback for older environments
  return Buffer.from(bytes).toString('utf-8');
}

// ============================================================================
// Hashing Functions
// ============================================================================

/**
 * Compute SHA-256 hash of data
 *
 * @param data - Input data as bytes or string
 * @returns SHA-256 hash (32 bytes)
 */
export function sha256Hash(data: Uint8Array | string): Uint8Array {
  const bytes = typeof data === 'string' ? stringToBytes(data) : data;
  return sha256(bytes);
}

/**
 * Compute SHA-256 hash and return as hex string
 *
 * @param data - Input data as bytes or string
 * @returns Hex-encoded SHA-256 hash
 */
export function sha256Hex(data: Uint8Array | string): string {
  return bytesToHex(sha256Hash(data));
}

/**
 * Compute SHA-256 hash and return as base64 string
 *
 * @param data - Input data as bytes or string
 * @returns Base64-encoded SHA-256 hash
 */
export function sha256Base64(data: Uint8Array | string): string {
  return bytesToBase64(sha256Hash(data));
}

// ============================================================================
// Random Bytes Generation
// ============================================================================

/**
 * Generate cryptographically secure random bytes
 *
 * @param length - Number of bytes to generate
 * @returns Random bytes
 */
export function randomBytes(length: number): Uint8Array {
  // Use Web Crypto API (available in all modern browsers and Node.js 19+)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    return crypto.getRandomValues(new Uint8Array(length));
  }

  // Fallback for older environments
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto.getRandomValues(new Uint8Array(length));
  }

  throw new Error('randomBytes: No crypto implementation available');
}

// ============================================================================
// Comparison Functions
// ============================================================================

/**
 * Constant-time byte array comparison
 *
 * @param a - First byte array
 * @param b - Second byte array
 * @returns True if arrays are equal, false otherwise
 */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i];
  }
  return result === 0;
}

/**
 * Concatenate multiple Uint8Arrays
 *
 * @param arrays - Arrays to concatenate
 * @returns Concatenated array
 */
export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);

  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }

  return result;
}

// ============================================================================
// Zero Functions (for secure memory cleanup)
// ============================================================================

/**
 * Zero out a Uint8Array (overwrite with zeros)
 *
 * Note: In JavaScript, we cannot guarantee memory is actually cleared
 * due to GC and potential copies, but we make a best effort.
 *
 * @param bytes - Byte array to zero
 */
export function zeroBytes(bytes: Uint8Array): void {
  bytes.fill(0);
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate hex string format
 *
 * @param hex - String to validate
 * @returns True if valid hex string
 */
export function isValidHex(hex: string): boolean {
  return /^(0x)?[0-9a-fA-F]*$/.test(hex) && hex.length % 2 === 0;
}

/**
 * Validate base64 string format
 *
 * @param base64 - String to validate
 * @returns True if valid base64 string
 */
export function isValidBase64(base64: string): boolean {
  try {
    base64ToBytes(base64);
    return true;
  } catch {
    return false;
  }
}

/**
 * Ensure data is a Uint8Array
 *
 * @param data - Data to validate (string or Uint8Array)
 * @param encoding - Encoding if data is string ('hex', 'base64', or 'utf8')
 * @returns Uint8Array
 */
export function ensureBytes(
  data: Uint8Array | string,
  encoding?: 'hex' | 'base64' | 'utf8'
): Uint8Array {
  if (data instanceof Uint8Array) {
    return data;
  }

  if (typeof data !== 'string') {
    throw new Error('Data must be Uint8Array or string');
  }

  switch (encoding) {
    case 'hex':
      return hexToBytes(data);
    case 'base64':
      return base64ToBytes(data);
    case 'utf8':
    default:
      return stringToBytes(data);
  }
}
